/**
 * Standard eventDescription builders for Contact Us / support audit trail.
 * Descriptions should state WHO did WHAT, on WHICH entity, with relevant context.
 */

function extractMessageFromApiPayload(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  const body = payload as { error?: unknown; message?: unknown };

  if (typeof body.error === "string" && body.error.trim()) {
    return body.error;
  }

  if (typeof body.message === "string" && body.message.trim()) {
    return body.message;
  }

  if (body.message != null && String(body.message).trim()) {
    return String(body.message);
  }

  return undefined;
}

function isHttpErrorResponseLike(
  value: object,
): value is { status?: number; message?: unknown; error?: unknown } {
  const candidate = value as { status?: unknown; message?: unknown };
  return (
    typeof candidate.status === "number" ||
    (typeof candidate.message === "string" &&
      candidate.message.startsWith("Http failure response"))
  );
}

function parseJsonErrorBody(body: string): unknown {
  try {
    return JSON.parse(body) as unknown;
  } catch {
    return body;
  }
}

function extractMessageFromErrorBody(body: unknown): string | undefined {
  if (typeof body === "string" && body.trim()) {
    return (
      extractMessageFromApiPayload(parseJsonErrorBody(body)) ??
      body.trim()
    );
  }

  return extractMessageFromApiPayload(body);
}

/**
 * Extracts a user-facing error string from HTTP/API failures for support history.
 * Backend formats supported:
 * - `{ "error": "Invalid status. Valid values are: ..." }`
 * - `{ "message": "No active Adjustments_Damaged claim found for provided identifiers." }`
 */
export function extractSupportErrorMessage(error: unknown): string {
  if (error == null) {
    return "Unknown error";
  }

  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }

  if (typeof error === "object") {
    const err = error as { message?: unknown; error?: unknown; status?: unknown };

    // HttpErrorResponse: API body is in `.error` — always prefer over transport `.message`
    const fromResponseBody = extractMessageFromErrorBody(err.error);
    if (fromResponseBody) {
      return fromResponseBody;
    }

    // Plain API body (not wrapped in HttpErrorResponse)
    if (!isHttpErrorResponseLike(err)) {
      const fromDirectBody = extractMessageFromApiPayload(error);
      if (fromDirectBody) {
        return fromDirectBody;
      }
    }

    if (typeof err.message === "string" && err.message.trim()) {
      return err.message.trim();
    }
  }

  return String(error);
}

const SUPPORT_FAILURE_EVENT_ACTIONS = new Set([
  "action_failed",
  "data_fetch_failed",
  "form_submit_failed",
  "report_download_failed",
]);

/** Resolves backend error text for failure events in support context history. */
export function resolveSupportContextError(
  eventAction: string,
  error?: unknown,
): string | undefined {
  if (error === undefined || !SUPPORT_FAILURE_EVENT_ACTIONS.has(eventAction)) {
    return undefined;
  }

  return extractSupportErrorMessage(error);
}

export function formatSupportContext(
  parts: Record<string, string | number | null | undefined>,
): string {
  const formatted = Object.entries(parts)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${key}=${value}`)
    .join(", ");

  return formatted ? ` (${formatted})` : "";
}

/** View finished rendering and is interactive. */
export function describeViewReady(
  viewName: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `${viewName} finished loading and is ready for interaction${formatSupportContext(context ?? {})}`;
}

/** User navigated to a route (resolver or component entry). */
export function describeRouteEntered(
  viewName: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User opened ${viewName}${formatSupportContext(context ?? {})}`;
}

/** User applied/changed list filters (before API load). */
export function describeFilterApplied(
  viewName: string,
  parts: Array<string | null | undefined>,
): string {
  const active = parts.filter((part) => !!part);

  if (active.length === 0) {
    return `User reset ${viewName} filters back to default values`;
  }

  return `User changed ${viewName} filters (${active.join(", ")})`;
}

/** User cleared all filters. */
export function describeFilterCleared(viewName: string): string {
  return `User cleared all active filters on ${viewName} and restored defaults`;
}

/** List/grid data loaded successfully. */
export function describeListLoaded(
  viewName: string,
  itemCount: number,
  reason: "filter" | "page" | "initial" | "refresh" = "filter",
): string {
  switch (reason) {
    case "page":
      return `${viewName} loaded ${itemCount} row(s) for the selected page`;
    case "initial":
      return `${viewName} initial data load completed with ${itemCount} row(s)`;
    case "refresh":
      return `${viewName} refreshed and returned ${itemCount} row(s)`;
    default:
      return `${viewName} reload completed after filter change — ${itemCount} row(s) returned`;
  }
}

/** User changed pagination. */
export function describePageChanged(
  viewName: string,
  pageIndex: number,
  pageSize: number,
  tab?: "report" | "history" | "list",
): string {
  const tabLabel =
    tab === "report"
      ? "report tab"
      : tab === "history"
        ? "export history tab"
        : "list";

  return `User changed ${viewName} ${tabLabel} pagination to page ${pageIndex} (${pageSize} rows per page)`;
}

/** User opened a modal or dialog. */
export function describeModalOpened(
  modalName: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User opened ${modalName}${formatSupportContext(context ?? {})}`;
}

/** User confirmed an action inside a modal. */
export function describeModalConfirmed(
  action: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User confirmed ${action} in dialog${formatSupportContext(context ?? {})}`;
}

/** User cancelled or dismissed a modal without completing. */
export function describeModalCancelled(
  modalName: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User cancelled ${modalName}${formatSupportContext(context ?? {})}`;
}

/** User submitted a form. */
export function describeFormSubmitted(
  formName: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User submitted ${formName}${formatSupportContext(context ?? {})}`;
}

/** API call initiated by user action. */
export function describeApiRequestStarted(
  operation: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `API request started: ${operation}${formatSupportContext(context ?? {})}`;
}

/** API call completed successfully. */
export function describeApiRequestSucceeded(
  operation: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `API request succeeded: ${operation}${formatSupportContext(context ?? {})}`;
}

/** API call failed. */
export function describeApiRequestFailed(
  operation: string,
  error: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `API request failed: ${operation}${formatSupportContext(context ?? {})} — ${error}`;
}

/** User navigated to a detail view. */
export function describeNavigatedToDetail(
  entity: string,
  context: Record<string, string | number | null | undefined>,
): string {
  return `User opened ${entity} detail view${formatSupportContext(context)}`;
}

/** User started create-case flow. */
export function describeStartedCreateCase(
  context: Record<string, string | number | null | undefined>,
): string {
  return `User started the create-case workflow${formatSupportContext(context)}`;
}

/** User opened an external link. */
export function describeExternalLinkOpened(
  linkType: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User opened external ${linkType} link${formatSupportContext(context ?? {})}`;
}

/** Export/report job created. */
export function describeExportJobCreated(
  reportName: string,
  format: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User queued ${reportName} export job${formatSupportContext({ format, ...context })}`;
}

/** User started downloading a report file. */
export function describeReportDownloadStarted(
  jobId: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User started downloading exported report file${formatSupportContext({ jobId, ...context })}`;
}

/** Report download failed. */
export function describeReportDownloadFailed(
  jobId: string,
  error: string,
): string {
  return describeApiRequestFailed("download exported report", error, { jobId });
}

/** WebSocket job status update received. */
export function describeWebsocketJobUpdate(
  reportName: string,
  messageType: string,
  jobId: string,
): string {
  return `WebSocket notified ${reportName} export job update${formatSupportContext({ messageType, jobId })}`;
}

/** User performed a search. */
export function describeSearchPerformed(
  searchTarget: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User searched ${searchTarget}${formatSupportContext(context ?? {})}`;
}

/** Generic UI state change (edit mode, panel toggle, etc.). */
export function describeUiStateChanged(
  change: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `User changed UI state: ${change}${formatSupportContext(context ?? {})}`;
}

/** View data refreshed after an in-place action. */
export function describeViewRefreshed(
  viewName: string,
  reason: string,
  context?: Record<string, string | number | null | undefined>,
): string {
  return `${viewName} data refreshed after ${reason}${formatSupportContext(context ?? {})}`;
}

/** @deprecated Use describeFilterApplied */
export const describeFilterChange = describeFilterApplied;

/** @deprecated Use describeListLoaded */
export const describeLoadedAfterFilter = (
  viewName: string,
  itemCount: number,
) => describeListLoaded(viewName, itemCount, "filter");
